import _ from 'lodash';
import React, { useMemo } from 'react';
import { Divider } from 'src/@vymo/ui/atoms';
import { NoData, Skelton } from 'src/@vymo/ui/blocks';
import { getDetails } from 'src/models/details/selectors';
import { getRevyreConfig } from 'src/models/lmsConfig/selectors';
import { useAppSelector } from 'src/store/hooks';
import { TAB_STATUS_KEYS } from '../constant';
import { getDetailsQuickAction, getStatus } from '../selector';
import ProfileActions from './profileActions';
import ProfileInputs from './profileInputs';
import ProfileTitle from './profileTitle';
import {
  getProfileCardFormValues,
  getProfileInputs,
  getProfileName,
  getWorkflowCode,
  shouldShowQuickActions,
} from './queries';
import styles from '../index.module.scss';

function ProfileCard(props) {
  const { entityId, domainType, workflowType } = props;
  const details = useAppSelector((state) => getDetails(state, entityId));

  const status = useAppSelector((state) =>
    getStatus(state, TAB_STATUS_KEYS.DETAILS),
  );

  const revyreConfig = useAppSelector(getRevyreConfig);
  const profileInputs = useMemo(
    () => getProfileInputs(props, details, revyreConfig),
    [details, props, revyreConfig],
  );
  const profileName = getProfileName(props, details);
  // TODO:: Implement lead tag & status for legacy modules
  const leadStatus = '';
  const leadTag = '';

  const profileCardFormValues = useMemo(() => {
    if (!profileInputs.length) return {};
    return getProfileCardFormValues(props, profileInputs, revyreConfig);
  }, [profileInputs, props, revyreConfig]);

  const showQuickActions = shouldShowQuickActions({
    inputsMap: details?.inputsMap,
    workflowType: details?.inputsMap?.workflowType || workflowType,
  });

  const quickActions = useAppSelector((state) =>
    getDetailsQuickAction(
      state,
      domainType === 'workflow' || !props?.revyre
        ? entityId
        : getWorkflowCode(details?.inputsMap?.workflowId),
      props,
    ),
  );

  const profileCardViews = useMemo(() => {
    const views: React.ReactNode[] = [];
    if (profileName) {
      views.push(
        <ProfileTitle
          profileName={profileName}
          leadStatus={leadStatus}
          leadTag={leadTag}
          {...props}
        />,
      );
    }

    if (profileInputs?.length && !_.isEmpty(profileCardFormValues)) {
      views.push(
        <ProfileInputs
          inputs={profileInputs}
          formValues={profileCardFormValues}
          {...props}
        />,
      );
    }

    if (quickActions?.length && showQuickActions) {
      views.push(
        <ProfileActions
          actions={quickActions}
          viewsCount={views?.length}
          {...props}
        />,
      );
    }
    return views;
  }, [
    profileCardFormValues,
    profileInputs,
    profileName,
    props,
    quickActions,
    showQuickActions,
  ]);

  if (!profileCardViews.length && status === 'done') return null;
  return (
    <div className={styles.detailsWrapper__profileCard}>
      {status === 'loading' && <Skelton avtar rounded lines={4} />}
      {status === 'error' && <NoData message="Error fetching entity details" />}
      {status === 'done' && (
        <>
          {profileCardViews.map((view, idx) => (
            <>
              {view}
              {idx !== profileCardViews.length - 1 && <Divider />}
            </>
          ))}
        </>
      )}
    </div>
  );
}

export default ProfileCard;
